import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "../styles/ResultPage.css";
import "../styles/ResultMobile.css";
import { apiUrl } from "../config";
import { useTranslation } from "react-i18next";
import { translateText } from "../utils/translateText";

function getLastLocalEntry() {
  try {
    const raw = localStorage.getItem("hwai_history");
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || !arr.length) return null;
    return arr[arr.length - 1];
  } catch {
    return null;
  }
}

const num = (v, f = null) => (Number.isFinite(+v) ? +v : f);
const cap = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);

export default function ResultPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const loc = useLocation();
  const navigate = useNavigate();

  const fromState = (loc && loc.state) || null;
  const state = fromState || getLastLocalEntry() || {};

  const bmi = useMemo(() => num(state?.bmi, 0), [state?.bmi]);
  const age = useMemo(() => num(state?.age), [state?.age]);
  const height = useMemo(() => num(state?.height), [state?.height]);
  const weight = useMemo(() => num(state?.weight), [state?.weight]);
  const gender = (state?.gender || "").toString();
  const zodiac = (state?.zodiac || localStorage.getItem("userZodiac") || "").toString().toLowerCase();
  const hasError = Boolean(state?.error);

  const [advice, setAdvice] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [horo, setHoro] = useState(null);
  const [horoErr, setHoroErr] = useState("");
  const [horoLoading, setHoroLoading] = useState(false);
  

  const status = bmi < 18.5
    ? { text: t("mvp.bmi_below_text"), label: t("mvp.bmi_below"), tone: "low" }
    : bmi < 25
      ? { text: t("mvp.bmi_healthy_text"), label: t("mvp.bmi_healthy"), tone: "healthy" }
      : bmi < 30
        ? { text: t("mvp.bmi_above_text"), label: t("mvp.bmi_above"), tone: "high" }
        : { text: t("mvp.bmi_higher_text"), label: t("mvp.bmi_higher"), tone: "high" };

  useEffect(() => {
    if (!bmi) {
      setAdvice(null);
      setErr("");
      return;
    }

    const ctrl = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setErr("");

        const response = await fetch(apiUrl("/api/bmi-advice"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bmi, age, gender, heightCm: height, weightKg: weight }),
          signal: ctrl.signal,
        });

        const data = await response.json();

        if (typeof data?.advice === "string") {
          const translated = ["es", "vi","ta"].includes(language) ? await translateText(data.advice, language) : data.advice;
          setAdvice({ summary: translated });
        } else if (data?.advice?.summary) {
          const { summary, dietTips, activityTips, cautions } = data.advice;
          const translatedSummary = ["es", "vi","ta"].includes(language) ? await translateText(summary, language) : summary;

          setAdvice({
            summary: translatedSummary,
            dietTips: dietTips || [],
            activityTips: activityTips || [],
            cautions: cautions || [],
          });
        } else {
          setErr(t("personalized_tips_error"));
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Tips fetch failed", e);
          setErr(t("personalized_tips_error"));
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [bmi, age, gender, height, weight, t, language]);

  useEffect(() => {
    const storedProfile = JSON.parse(localStorage.getItem("profileData"));
    const fallbackZodiac = zodiac || storedProfile?.zodiac;
    if (!fallbackZodiac) return;

    const ctrl = new AbortController();
    (async () => {
      try {
        setHoroLoading(true);
        const q = `sign=${encodeURIComponent(fallbackZodiac)}&day=today`;
        const response = await fetch(apiUrl(`/api/horoscope?${q}`), { signal: ctrl.signal });
        const data = await response.json();

        if (data?.description) {
          const translated = ["es", "vi","ta"].includes(language) ? await translateText(data.description, language) : data.description;
          setHoro({ sign: data.sign, day: data.day, description: translated });
        } else {
          setHoroErr(t("horoscope_error"));
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Horoscope fetch failed", e);
          setHoroErr(t("horoscope_error"));
        }
      } finally {
        setHoroLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [zodiac, t, language]);

  if (!fromState && !getLastLocalEntry()) {
    navigate("/tool");
    return null;
  }

  return (
    <>
      <Navbar />
      <div className="result-container">
        <section className="result-wrapper">
          <div className="result-center">
            {hasError ? (
              <div className="card">
                <h2 className="result-heading" style={{ color: "tomato" }}>{t("error_message")}</h2>
                <button className="btn-outline" onClick={() => navigate("/tool")}>{t("go_back")}</button>
              </div>
            ) : (
              <>
                <div className={`result-summary result-${status.tone}`}>
                  <div className="bmi-score"><small>{t("your_bmi")}</small><strong>{bmi}</strong><span>{status.label}</span></div>
                  <div><div className="result-kicker">{t("mvp.snapshot")}</div><h2 className="result-heading">{status.text}</h2><p>{t("mvp.bmi_disclaimer")}</p></div>
                </div>
                <div className="card input-summary">
                  <div className="stat-row"><span className="stat-label">{t("mvp.age")}</span><span className="stat-value">{age || "—"}</span></div>
                  <div className="stat-row"><span className="stat-label">{t("mvp.height")}</span><span className="stat-value">{height || "—"} {t("cm")}</span></div>
                  <div className="stat-row"><span className="stat-label">{t("mvp.weight")}</span><span className="stat-value">{weight || "—"} {t("kg")}</span></div>
                </div>

                <div className="tips-horo-wrapper">
                  <div className="card">
                    <h3 className="accent-title">{t("personalized_tips")}</h3>
                    {loading ? (
                      <p className="muted">{t("generating_tips")}</p>
                    ) : err ? (
                      <p className="warn">{err}</p>
                    ) : advice ? (
                      <div className="tips-grid">
                        <p><strong>{advice.summary}</strong></p>

                        {advice.dietTips?.length > 0 && (
                          <>
                            <h4>{t("diet_tips")}</h4>
                            <ul>{advice.dietTips.map((tip, i) => <li key={`diet-${i}`}>{tip}</li>)}</ul>
                          </>
                        )}
                        {advice.activityTips?.length > 0 && (
                          <>
                            <h4>{t("activity_tips")}</h4>
                            <ul>{advice.activityTips.map((tip, i) => <li key={`act-${i}`}>{tip}</li>)}</ul>
                          </>
                        )}
                        {advice.cautions?.length > 0 && (
                          <>
                            <h4>{t("caution_notes")}</h4>
                            <ul>{advice.cautions.map((tip, i) => <li key={`caution-${i}`}>{tip}</li>)}</ul>
                          </>
                        )}

                        <small className="muted">{t("medical_disclaimer")}</small>
                      </div>
                    ) : (
                      <p className="muted">{t("personalized_tips_error")}</p>
                    )}
                  </div>

                  <div className="card">
                    <h3 className="accent-title">{t("todays_luck")} — {cap(zodiac)}</h3>
                    {!zodiac ? (
                      <p className="muted">{t("no_zodiac")}</p>
                    ) : horoLoading ? (
                      <p className="muted">{t("checking_stars")}</p>
                    ) : horoErr ? (
                      <p className="warn">{horoErr}</p>
                    ) : horo && horo.description ? (
                      <div className="tips-grid">
                        <p>{horo.description}</p>
                        <small className="muted">{t("entertainment_disclaimer")}</small>
                      </div>
                    ) : (
                      <p className="muted">{t("horoscope_error")}</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <aside className="result-actions">
            <button className="circle" onClick={() => navigate("/healthy-foods")}>🗺️<br />{t("healthy_foods")}</button>
            <button className="circle" onClick={() => navigate("/nearby-gyms")}>🏃<br />{t("nearby_gyms")}</button>
            <button className="circle" onClick={() => navigate("/nearby-hospitals")}>🏥<br />{t("nearby_hospitals")}</button>
          </aside>
        </section>

        <footer className="rp-footer">© All rights reserved – Ranjith Gopinath</footer>
      </div>
    </>
  );
}
